import React, { useMemo, useContext, createContext } from 'react'
import PropTypes from 'prop-types'
import { useAvailability } from '../../utils/availability'
import { useMenus } from '../../hooks/use-menus'
import { useGetCart } from '../CartQuery/CartQuery'
import { maybeDeepFreeze } from 'apollo-utilities'
import { usePersistEqualObject } from '../../hooks/use-persist-equal-object'
import { usePopularItems } from '../../hooks/use-popular-items'
import { useUpsellUIState } from './use-upsell-ui-state/use-upsell-ui-state'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'

const getItemsFlat = menus => menus.reduce((acc, menu) => {
  const menuName = menu.name
  const items = menu.groups.reduce((itemsAcc, group) => {
    const groupName = group.name
    return itemsAcc.concat(group.items.map(item => ({ ...item, groupName, menuName })))
  }, [])
  return acc.concat(items)
}, [])

const noop = () => {}
const emptyContext = {
  canUpsell: false,
  setUpsellCallbacks: noop,
  upsells: []
}
const UpsellsContext = createContext(emptyContext)

const noFilterRules = [() => true]
const noSortRules = [() => 0]
const emptyArr = []
/**
 *
 * @param {object} props
 * @param {Array<import('./upsell-rules').filterFunctionWithContext>} props.filterRules
 * @param {Array<import('./upsell-rules').sortFunctionWithContext>} props.sortRules
 */
const UpsellsProviderInner = ({ children, filterRules = noFilterRules, sortRules = noSortRules }) => {
  if (filterRules.length === 0) filterRules = noFilterRules
  if (sortRules.length === 0) sortRules = noSortRules
  const { availability } = useAvailability()
  const { cart } = useGetCart()
  const { data: popularItemsData } = usePopularItems()

  const { menus = [] } = useMenus({ skip: !availability })
  const popularItems = (popularItemsData && popularItemsData.popularItems && popularItemsData.popularItems.items) || emptyArr

  // avoids redudundant calculations if rules have not changed
  const currentFilterRules = usePersistEqualObject(filterRules)
  const currentSortRules = usePersistEqualObject(sortRules)

  const upsells = useMemo(() => {
    const items = getItemsFlat(menus)
    const ctx = maybeDeepFreeze({ cart, popularItems })

    const filtered = currentFilterRules
      .reduce((acc, rule) => {
        return acc.filter((...args) => rule(ctx, ...args))
      }, items)

    const sorted = currentSortRules.reduce((acc, rule) => {
      return acc.sort((...args) => rule(ctx, ...args))
    }, filtered)

    return sorted
  }, [cart, currentFilterRules, currentSortRules, menus, popularItems])

  const upsellUIState = useUpsellUIState(upsells)

  const context = useMemo(() => ({
    ...upsellUIState,
    upsells
  }), [upsellUIState, upsells])

  return (
    <UpsellsContext.Provider value={context}>
      {children}
    </UpsellsContext.Provider>
  )
}

UpsellsProviderInner.propTypes = {
  children: PropTypes.node.isRequired,
  filterRules: PropTypes.arrayOf(PropTypes.func.isRequired),
  sortRules: PropTypes.arrayOf(PropTypes.func.isRequired)
}

export const UpsellsProvider = (props) => {
  const { restaurantInfo } = useRestaurant()

  let enabled = false

  if (
    restaurantInfo.data &&
    restaurantInfo.data.restaurant &&
    restaurantInfo.data.restaurant.upsellsConfig
  ) {
    enabled = Boolean(restaurantInfo.data.restaurant.upsellsConfig.enabled)
  }

  if (!enabled) {
    return (
      <UpsellsContext.Provider value={emptyContext}>
        {props.children}
      </UpsellsContext.Provider>
    )
  }

  return <UpsellsProviderInner {...props} />
}

UpsellsProvider.propTypes = {
  children: PropTypes.node
}

export const useUpsells = () => useContext(UpsellsContext)
